"use strict";
import { skipToken, useQuery } from "@tanstack/react-query";
import { UniverseChainId } from "features/chains/types";
import { createEthersProvider } from "features/providers/createEthersProvider";
import { areAddressesEqual } from "utils/addresses";
import { ReactQueryCacheKey } from "utilities/src/reactQuery/cache";
import { ONE_MINUTE_MS } from "utilities/src/time/time";
export var EnsLookupType = /* @__PURE__ */ ((EnsLookupType2) => {
  EnsLookupType2["Name"] = "name";
  EnsLookupType2["Address"] = "address";
  EnsLookupType2["Avatar"] = "avatar";
  EnsLookupType2["Description"] = "description";
  EnsLookupType2["TwitterUsername"] = "com.twitter";
  return EnsLookupType2;
})(EnsLookupType || {});
async function getNameFetch(address, provider) {
  const name = await provider.lookupAddress(address);
  const fwdAddr = name ? await provider.resolveName(name) : null;
  return areAddressesEqual(fwdAddr, address) ? name : null;
}
async function getAddressFetch(name, provider) {
  return await provider.resolveName(name);
}
async function getAvatarFetch(address, provider) {
  const name = await provider.lookupAddress(address);
  const fwdAddr = name ? await provider.resolveName(name) : null;
  const checkedName = areAddressesEqual(address, fwdAddr) ? name : null;
  return checkedName ? await provider.getAvatar(checkedName) : null;
}
async function getTextFetch(key, name, provider) {
  const resolver = await provider.getResolver(name);
  const text = resolver?.getText(key);
  return text ?? null;
}
async function getOnChainEnsFetch(params) {
  const { type, nameOrAddress } = params;
  const provider = createEthersProvider(UniverseChainId.Mainnet);
  if (!provider) {
    return null;
  }
  switch (type) {
    case "name" /* Name */:
      return await getNameFetch(nameOrAddress, provider);
    case "address" /* Address */:
      return await getAddressFetch(nameOrAddress, provider);
    case "avatar" /* Avatar */:
      return await getAvatarFetch(nameOrAddress, provider);
    case "description" /* Description */:
      return await getTextFetch("description", nameOrAddress, provider);
    case "com.twitter" /* TwitterUsername */:
      return await getTextFetch("com.twitter", nameOrAddress, provider);
    default:
      throw new Error(`Invalid ENS lookup type: ${type}`);
  }
}
function useEnsQuery(type, nameOrAddress) {
  return useQuery({
    queryKey: [ReactQueryCacheKey.OnchainENS, type, nameOrAddress],
    queryFn: nameOrAddress ? async () => await getOnChainEnsFetch({ type, nameOrAddress, chainId: UniverseChainId.Mainnet }) : skipToken,
    staleTime: 5 * ONE_MINUTE_MS
  });
}
export function useENSName(address) {
  return useEnsQuery("name" /* Name */, address);
}
export function useAddressFromEns(maybeName) {
  return useEnsQuery("address" /* Address */, maybeName);
}
export function useENSAvatar(address) {
  return useEnsQuery("avatar" /* Avatar */, address);
}
export function useENSDescription(name) {
  return useEnsQuery("description" /* Description */, name);
}
export function useENSTwitterUsername(name) {
  return useEnsQuery("com.twitter" /* TwitterUsername */, name);
}
